import { FormEventHandler, Fragment, MouseEventHandler, ReactElement, useEffect, useState } from 'react'
import { Label } from './label'

interface ICommonProps {
    autocomplete?: boolean
    disabled?: boolean
    extraClassNames?: string
    hidden?: boolean
    id?: string
    name?: string
    onInput?: FormEventHandler<HTMLInputElement>
    /**
     * Override default functionality of Enter key for this input field.
     * Use this in case you want to prevent form submissions and advance the UI instead, etc.
     */
    onEnterKeyPressed?: () => void
    placeholder?: string
    type?: "button" | "checkbox" | "color" | "date" | "datetime-local" | "email" | "file" | "hidden" | "image" | "month" | "number" | "password" | "radio" | "range" | "reset" | "search" | "submit" | "tel" | "text" | "time" | "url" | "week"
    value?: string
    step?: number
    leadingIcon?: ReactElement
    trailingButton?: React.ReactElement
}

interface InputProps extends ICommonProps {
    description?: string | JSX.Element
    isRequired?: boolean
    label?: string | JSX.Element
    buttonLabel?: string | JSX.Element
    validationErrors?: string[]
    onInputChanged?: (newValue: string) => void
    initialErrors?: string[]
    disableFullWidth?: boolean
    inputRef?: React.LegacyRef<any>
}
interface IState {
    inputValue: string
    validationErrors: string[]
}

interface IBareProps extends ICommonProps {
    inputRef?: React.LegacyRef<any>
    hasLeftButton?: boolean
    hasValidationError?: boolean
    disableFullWidth?: boolean
}

const InputBare = (props: IBareProps) => {
    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key == "Enter" && props.onEnterKeyPressed) {
            e.preventDefault()
            e.stopPropagation()
            props.onEnterKeyPressed()
        }
    }

    return (
        <div className={`relative flex items-center ${props.disableFullWidth ? '' : 'w-full'}`}>
            {props.leadingIcon && (
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {props.leadingIcon}
                </span>
            )}
            <input
                ref={props.inputRef}
                name={props.name}
                type={props.type ? props.type : "text"}
                className={`mt-1 text-slate-900 block ${props.disableFullWidth ? '' : 'w-full'} ${props.leadingIcon ? 'pl-10' : ''} ${props.trailingButton ? 'pr-10' : ''} ${props.hasLeftButton ? 'rounded-tl-md rounded-bl-md border-r-0 border-t border-l border-b' : 'rounded-md border'} bg-white focus:border-indigo-600 focus:bg-white focus:ring-0 disabled:bg-slate-100 disabled:text-slate-500 ${props.hasValidationError ? "border-rose-500" : "border-slate-300"} ${props.hidden ? "hidden" : ""} ${props.extraClassNames ? props.extraClassNames : ""}`}
                placeholder={props.placeholder}
                value={props.value}
                onInput={props.onInput}
                onKeyDown={onKeyDown}
                autoComplete={props.autocomplete ? "on" : "off"}
                data-lpignore={!props.autocomplete ? "true" : "false"}
                disabled={props.disabled}
                id={props.id}
                step={props.step}
            />
            {props.trailingButton && (
                <span className="absolute inset-y-0 right-0 pr-2 flex items-center">
                    {props.trailingButton}
                </span>
            )}
        </div>
    )
}

interface InputWithButtonProps extends InputProps {
    buttonAction: MouseEventHandler<HTMLButtonElement>
    buttonExtraClassNames?: string
}

export const InputWithButton = (props: InputWithButtonProps) => {
    const [hasValidationError, setHasValidationError] = useState((props.validationErrors || []).length > 0)

    useEffect(() => {
        setHasValidationError((props.validationErrors || []).length > 0)
    }, [props.initialErrors])

    return (
        <Fragment>
            <div className={`flex items-center ${props.extraClassNames || ""}`}>
                <InputBare
                    {...props}
                    hasLeftButton={true}
                    extraClassNames={undefined}
                    value={props.value}
                    onInput={(e) => {
                        if (props.onInputChanged) props.onInputChanged(e.currentTarget.value)
                    }}
                    hasValidationError={hasValidationError}
                />
                <button
                    className={`relative p-2 px-3 mt-1 text-xl leading-6 ${props.buttonExtraClassNames ? props.buttonExtraClassNames : ""} border inputbarebuttonleft rounded-tr-md rounded-br-md focus:outline-none focus:ring-0 ${hasValidationError ? "border-rose-500" : "border-gray-500"}`}
                    type="button"
                    onClick={props.buttonAction}
                    disabled={props.disabled}
                >
                    {props.buttonLabel}
                </button>
            </div>
            {props.description ? <p className={`mt-2 text-sm text-gray-600 ${props.extraClassNames ? props.extraClassNames : ""} `}>{props.description}</p> : null}
            {props.validationErrors?.map(validationError => (
                <p key={validationError} className="ml-3 text-sm text-left text-rose-500 md:col-start-2">{validationError}</p>
            ))}
        </Fragment>
    )
}

export const InputHorizontal = (props: InputProps) => {
    const [hasValidationError, setHasValidationError] = useState((props.initialErrors || []).length > 0)

    useEffect(() => {
        setHasValidationError((props.initialErrors || []).length > 0)
    }, [props.initialErrors])

    return (
        <label className={`block ${props.hidden ? 'hidden' : ''} ${props.extraClassNames ? props.extraClassNames : ""}`}>
            <div className='grid items-baseline grid-cols-1 mb-3 md:grid-cols-2 md:gap-4'>
                {props.label && <span className={`md:text-right ${props.isRequired ? "font-bold" : "font-normal"}`}>{props.label}</span>}
                <div>
                    <InputBare
                        {...props}
                        extraClassNames={undefined}
                        value={props.value}
                        onInput={(e) => {
                            if (props.onInputChanged) props.onInputChanged(e.currentTarget.value)
                        }}
                        hasValidationError={hasValidationError}
                    />
                    {props.validationErrors.map(validationError => (
                        <p key={validationError} className="ml-3 text-sm text-left text-rose-500 md:col-start-2">{validationError}</p>
                    ))}
                </div>
            </div>

            {(props.description) && <span className="text-sm leading-tight text-gray-500">{props.description}</span>}
        </label>
    )
}


export const Input = (props: InputProps) => {
    const [hasValidationError, setHasValidationError] = useState((props.initialErrors || []).length > 0)

    useEffect(() => {
        setHasValidationError((props.initialErrors || []).length > 0)
    }, [props.initialErrors])

    const inputBareComponent = (
        <InputBare
            {...props}
            value={props.value}
            onInput={(e) => {
                if (props.onInputChanged) props.onInputChanged(e.currentTarget.value)
            }}
            hasValidationError={hasValidationError}
        />
    );

    if (!props.label) {
        return inputBareComponent;
    }

    return (
        <Label isRequired={props.isRequired} label={props.label} hidden={props.hidden} description={props.description} extraClassNames={props.extraClassNames}>
            <div className="mt-1">
                {inputBareComponent}
                {props.validationErrors?.map(validationError => (
                    <p key={validationError} className="text-sm text-rose-500">{validationError}</p>
                ))}
                {/*(props.description) && <span className="text-sm leading-tight text-gray-500"> {props.description}</span>*/}
            </div>
        </Label>
    )
}

export const InputOnly = (props: InputProps) => {
    const [hasValidationError, setHasValidationError] = useState((props.initialErrors || []).length > 0)

    return (
        <Fragment>
            <InputBare
                {...props}
                extraClassNames={props.extraClassNames}
                value={props.value}
                onInput={(e) => {
                    props.onInputChanged(e.currentTarget.value)
                }}
                hasValidationError={hasValidationError}
            />
            {props.validationErrors?.map(validationError => (
                <p key={validationError} className={`text-sm text-rose-500 ${props.extraClassNames ? props.extraClassNames : ''}`}>{validationError}</p>
            ))}
        </Fragment>
    )
}
